import {
  Box,
  Grid,
  SvgIcon,
  Typography,
  useTheme,
} from '@mui/material';
import { TableColumn } from '../../../../../shared/types';
import { StateAlert } from '../../../components/Common/StateAlert';
import getSheetState from '../../../helpers/getSheetState';
import {
  getPricingSheetStateSeverity,
  getSheetStateIcon,
} from '../helpers';
import { formatDateTolocaleDateString } from '../../../utils/formatDateTimes';
import DefaultTooltip from '../../../components/Tooltips/DefaultTooltip';
import {
  differenceInCalendarDays,
  isWithinInterval,
  subDays,
} from 'date-fns';
import { getDateWithoutTime } from '../../../../../shared/utils/getDateWithoutTime';
import { PricingSheetTableData } from '../../../../../shared/types/pricingSheets';
import GroupIcon from '@mui/icons-material/Group';
import removePricingTeamEmail from '../../../../../shared/helpers/removePricingTeamEmail';
import { Inventory2Outlined } from '@mui/icons-material';

export const PricingSheetTableCellComponent = ({
  item,
  column,
}: {
  item: PricingSheetTableData;
  column: TableColumn<PricingSheetTableData>;
}) => {
  const theme = useTheme();
  const { key } = column;
  switch (key) {
    case 'state':
      const isArchived = !!item.archivedAt;
      const severity = isArchived
        ? 'warning'
        : getPricingSheetStateSeverity(item.state);
      const label = getSheetState(item.state);
      const icon = isArchived
        ? Inventory2Outlined
        : getSheetStateIcon(item.state);

      return (
        <StateAlert
          backgroundColor={isArchived ? '#f2f2f2' : ''}
          textColor={isArchived ? '#000000de' : ''}
          severity={severity}
          label={isArchived ? `(${label})` : label}
          icon={
            <SvgIcon
              sx={{
                fontSize: 'inherit',
              }}
              component={icon}
            />
          }
        />
      );
    case 'createdAt':
      return (
        <Typography whiteSpace={'nowrap'} fontSize={'inherit'}>
          {item[key] && formatDateTolocaleDateString(item[key])}
        </Typography>
      );
    case 'to': {
      const value = item[key];

      if (!value) {
        return;
      }

      const currentDateWithoutTime = getDateWithoutTime(
        new Date(),
      ).getTime();
      const offerValidityEndWithoutTime =
        getDateWithoutTime(value).getTime();

      const hasExpired =
        offerValidityEndWithoutTime < currentDateWithoutTime;
      const warningDaysBeforeExpirationLimit = 60;

      // The three days: today, tomorrow or day after that
      const expiresInTwoWeeks = isWithinInterval(
        currentDateWithoutTime,
        {
          start: subDays(
            offerValidityEndWithoutTime,
            warningDaysBeforeExpirationLimit,
          ),
          end: offerValidityEndWithoutTime,
        },
      );
      const severity = hasExpired
        ? 'error'
        : expiresInTwoWeeks
          ? 'warning'
          : 'success';
      const infoTooltipText = hasExpired
        ? `Hinnasto on vanhentunut!`
        : expiresInTwoWeeks
          ? `Hinnasto vanhenee ${differenceInCalendarDays(offerValidityEndWithoutTime, currentDateWithoutTime) + 1} päivän sisällä.`
          : null;

      return (
        <StateAlert
          severity={severity}
          label={formatDateTolocaleDateString(value)}
          {...(infoTooltipText && {
            infoTooltipColor: 'info',
            infoTooltipText,
          })}
          // remove background color
          sx={{
            backgroundColor: 'transparent',
            color: 'text.primary',
            p: 0,
            '.MuiAlert-message': {
              [theme.breakpoints.down('xl')]: {
                fontSize: '0.75rem',
              },
            },
          }}
        />
      );
    }
    case 'responsiblePerson': {
      return (
        <Grid
          container
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignContent={'center'}
          width={'100%'}
        >
          {removePricingTeamEmail(item.editingRights) ? (
            // Note: The box is a workaround to get the element inside tooltip to take 100% space
            <Box
              sx={{
                width: '100%',
                '& > span': { display: 'block', width: '100%' },
              }}
            >
              <DefaultTooltip
                placement="top"
                enterDelay={400}
                enterNextDelay={400}
                title={
                  'Vastuullinen: ' +
                    item[column.key] +
                    '\n' +
                    'Hinnasto jaettu: ' +
                    removePricingTeamEmail(item.editingRights) || ''
                }
                slotProps={{
                  popper: { sx: { whiteSpace: 'pre-line' } },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item[column.key]}
                  </Box>
                  <GroupIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                </Box>
              </DefaultTooltip>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                '& > span': { display: 'block', width: '100%' },
              }}
            >
              <DefaultTooltip
                placement="top"
                enterDelay={400}
                enterNextDelay={400}
                title={'Vastuullinen: ' + item[column.key]}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item[column.key]}
                  </Box>
                </Box>
              </DefaultTooltip>
            </Box>
          )}
        </Grid>
      );
    }
    case 'id': {
      return item[column.key];
    }

    case 'name': {
      return (
        <Grid container flexDirection={'row'} alignContent={'center'}>
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: 'max(9.5vw, 400px)',
            }}
          >
            <DefaultTooltip
              placement={'top'}
              enterDelay={400}
              enterNextDelay={400}
              title={item[column.key] || ''}
            >
              <span>{item[column.key]}</span>
            </DefaultTooltip>
          </Box>
        </Grid>
      );
    }

    default:
      return (
        <Grid container flexDirection={'row'} alignContent={'center'}>
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: 'max(9.5vw, 125px)',
            }}
          >
            <DefaultTooltip
              placement={'top'}
              enterDelay={400}
              enterNextDelay={400}
              title={item[column.key] || ''}
            >
              <span>{item[column.key]}</span>
            </DefaultTooltip>
          </Box>
        </Grid>
      );
  }
};
